import React, { useState } from 'react';
import { Condition, ConditionGroup, Document, ConditionStatus, ConditionHistoryEntry, ConditionComment, CommentVisibility } from './types';
import { StatusSummaryCards } from './components/StatusSummaryCards';
import { ConditionTable } from './components/ConditionTable';
import { AddConditionForm } from './components/AddConditionForm';
import { DocumentAssignmentModal } from './components/DocumentAssignmentModal';
import { NotesHistoryPanel } from './components/NotesHistoryPanel';
import { MessageSquareIcon } from 'lucide-react';
// Seed data
const initialConditions: Condition[] = [{
  id: 'cond-1',
  name: 'Settlement Certification',
  description: 'Settlement Agent and Seller needs to sign Settlement Certification.',
  status: 'new',
  createdAt: new Date('2024-12-09'),
  assignedTo: 'Rama Kacho',
  groupId: 'group-1',
  history: [{
    type: 'created',
    timestamp: new Date('2024-12-09')
  }, {
    type: 'group_added',
    timestamp: new Date('2024-12-09'),
    toGroupId: 'group-1',
    toGroupName: 'Must be completed before scheduled closing date of Jan 15'
  }]
}, {
  id: 'cond-2',
  name: 'Proof of Homeowners Insurance',
  description: 'Borrower must provide current homeowners insurance declaration page.',
  status: 'requested',
  createdAt: new Date('2024-12-05'),
  requestedAt: new Date('2024-12-06'),
  assignedTo: 'Rama Kacho',
  groupId: 'group-1',
  history: [{
    type: 'created',
    timestamp: new Date('2024-12-05')
  }, {
    type: 'status_change',
    timestamp: new Date('2024-12-06'),
    fromStatus: 'new',
    toStatus: 'requested'
  }, {
    type: 'group_added',
    timestamp: new Date('2024-12-07'),
    toGroupId: 'group-1',
    toGroupName: 'Must be completed before scheduled closing date of Jan 15'
  }]
}, {
  id: 'cond-3',
  name: 'Final Inspection Report',
  description: 'Property inspection must be completed and report submitted.',
  status: 'review',
  createdAt: new Date('2024-12-01'),
  requestedAt: new Date('2024-12-02'),
  assignedTo: 'Sarah Chen',
  groupId: undefined,
  history: [{
    type: 'created',
    timestamp: new Date('2024-12-01')
  }, {
    type: 'status_change',
    timestamp: new Date('2024-12-02'),
    fromStatus: 'new',
    toStatus: 'requested'
  }, {
    type: 'group_added',
    timestamp: new Date('2024-12-03'),
    toGroupId: 'group-2',
    toGroupName: 'Title & Compliance'
  }, {
    type: 'group_moved',
    timestamp: new Date('2024-12-04'),
    fromGroupId: 'group-2',
    fromGroupName: 'Title & Compliance',
    toGroupId: 'group-1',
    toGroupName: 'Prior to Closing'
  }, {
    type: 'group_removed',
    timestamp: new Date('2024-12-05'),
    fromGroupId: 'group-1',
    fromGroupName: 'Prior to Closing'
  }, {
    type: 'status_change',
    timestamp: new Date('2024-12-06'),
    fromStatus: 'requested',
    toStatus: 'review'
  }]
}, {
  id: 'cond-4',
  name: 'Title Search Results',
  description: 'Title company to provide clear title search results.',
  status: 'new',
  createdAt: new Date('2024-12-10'),
  assignedTo: 'Mike Torres',
  groupId: 'group-2',
  history: [{
    type: 'created',
    timestamp: new Date('2024-12-10')
  }, {
    type: 'group_added',
    timestamp: new Date('2024-12-10'),
    toGroupId: 'group-2',
    toGroupName: 'Title & Compliance'
  }]
}, {
  id: 'cond-5',
  name: 'Flood Zone Determination',
  description: 'FEMA flood zone certification required.',
  status: 'cleared',
  createdAt: new Date('2024-11-28'),
  assignedTo: 'Sarah Chen',
  groupId: undefined,
  satisfiedByDocumentId: 'doc-1',
  history: [{
    type: 'created',
    timestamp: new Date('2024-11-28')
  }, {
    type: 'status_change',
    timestamp: new Date('2024-12-01'),
    fromStatus: 'new',
    toStatus: 'cleared'
  }]
}, {
  id: 'cond-6',
  name: 'VOE - Verbal Verification of Employment',
  description: 'Verbal verification of employment within 10 days of closing.',
  status: 'new',
  createdAt: new Date('2024-12-11'),
  assignedTo: 'Rama Kacho',
  groupId: 'group-2',
  history: [{
    type: 'created',
    timestamp: new Date('2024-12-11')
  }, {
    type: 'group_added',
    timestamp: new Date('2024-12-11'),
    toGroupId: 'group-2',
    toGroupName: 'Title & Compliance'
  }]
}, {
  id: 'cond-7',
  name: 'Updated Credit Report',
  description: 'Pull updated credit report — previous one expired.',
  status: 'requested',
  createdAt: new Date('2024-12-03'),
  requestedAt: new Date('2024-12-04'),
  assignedTo: 'Mike Torres',
  groupId: undefined,
  history: [{
    type: 'created',
    timestamp: new Date('2024-12-03')
  }, {
    type: 'status_change',
    timestamp: new Date('2024-12-04'),
    fromStatus: 'new',
    toStatus: 'requested'
  }]
}];
const initialGroups: ConditionGroup[] = [{
  id: 'group-1',
  name: 'Prior to Closing',
  note: 'Must be completed before scheduled closing date of Jan 15',
  isExpanded: true,
  createdAt: new Date('2024-12-08'),
  noteHistory: [{
    note: 'Must be completed before scheduled closing date of Jan 15',
    timestamp: new Date('2024-12-08')
  }]
}, {
  id: 'group-2',
  name: 'Title & Compliance',
  note: '',
  isExpanded: true,
  createdAt: new Date('2024-12-09'),
  noteHistory: []
}];
const initialDocuments: Document[] = [{
  id: 'doc-1',
  name: 'FEMA Flood Certificate.pdf',
  conditionIds: ['cond-5'],
  createdAt: new Date('2024-12-01')
}];
export function App() {
  const [conditions, setConditions] = useState<Condition[]>(initialConditions);
  const [groups, setGroups] = useState<ConditionGroup[]>(initialGroups);
  const [documents, setDocuments] = useState<Document[]>(initialDocuments);
  const [isAddingCondition, setIsAddingCondition] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isNotesHistoryOpen, setIsNotesHistoryOpen] = useState(false);
  // Derived state
  const counts = {
    new: conditions.filter(c => c.status === 'new').length,
    requested: conditions.filter(c => c.status === 'requested').length,
    review: conditions.filter(c => c.status === 'review').length,
    cleared: conditions.filter(c => c.status === 'cleared').length
  };
  const activeConditions = conditions.filter(c => c.status !== 'cleared');
  const clearedConditions = conditions.filter(c => c.status === 'cleared');
  // Condition Actions
  const addCondition = (data: {
    name: string;
    description: string;
    assignedTo: string;
    groupId?: string;
  }) => {
    const now = new Date();
    const historyEntries: ConditionHistoryEntry[] = [{
      type: 'created',
      timestamp: now
    }];
    if (data.groupId) {
      const targetGroup = groups.find(g => g.id === data.groupId);
      historyEntries.push({
        type: 'group_added',
        timestamp: now,
        toGroupId: data.groupId,
        toGroupName: targetGroup?.note || targetGroup?.name || data.groupId
      });
    }
    const newCond: Condition = {
      id: crypto.randomUUID(),
      name: data.name,
      description: data.description,
      assignedTo: data.assignedTo,
      groupId: data.groupId,
      status: 'new',
      createdAt: now,
      history: historyEntries
    };
    setConditions(prev => [newCond, ...prev]);
    setIsAddingCondition(false);
  };
  const removeCondition = (id: string) => {
    setConditions(prev => prev.filter(c => c.id !== id));
  };
  const updateConditionStatus = (id: string, status: ConditionStatus) => {
    setConditions(prev => prev.map(c => {
      if (c.id !== id) return c;
      const now = new Date();
      const updates: Partial<Condition> = {
        status
      };
      if (status === 'requested' && !c.requestedAt) updates.requestedAt = now;
      const historyEntry: ConditionHistoryEntry = {
        type: 'status_change',
        timestamp: now,
        fromStatus: c.status,
        toStatus: status
      };
      return {
        ...c,
        ...updates,
        history: [...(c.history ?? []), historyEntry]
      };
    }));
  };
  const handleMoveToGroup = (conditionIds: string[], groupId: string | undefined) => {
    const now = new Date();
    const targetGroup = groupId ? groups.find(g => g.id === groupId) : undefined;
    setConditions(prev => prev.map(c => {
      if (!conditionIds.includes(c.id)) return c;
      const oldGroup = c.groupId ? groups.find(g => g.id === c.groupId) : undefined;
      let entry: ConditionHistoryEntry;
      if (!c.groupId && groupId) {
        // Adding to a group
        entry = {
          type: 'group_added',
          timestamp: now,
          toGroupId: groupId,
          toGroupName: targetGroup?.note || targetGroup?.name || groupId
        };
      } else if (c.groupId && !groupId) {
        // Removing from a group
        entry = {
          type: 'group_removed',
          timestamp: now,
          fromGroupId: c.groupId,
          fromGroupName: oldGroup?.note || oldGroup?.name || c.groupId
        };
      } else {
        // Moving between groups
        entry = {
          type: 'group_moved',
          timestamp: now,
          fromGroupId: c.groupId,
          fromGroupName: oldGroup?.note || oldGroup?.name || c.groupId,
          toGroupId: groupId,
          toGroupName: targetGroup?.note || targetGroup?.name || groupId
        };
      }
      return {
        ...c,
        groupId,
        history: [...(c.history ?? []), entry]
      };
    }));
  };
  const handleUpdateNote = (groupId: string, newNote: string) => {
    const now = new Date();
    const targetGroup = groups.find(g => g.id === groupId);
    const oldNote = targetGroup?.note ?? '';
    if (oldNote === newNote) return;
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      const history = g.noteHistory ?? [];
      const updatedHistory = [...history, {
        note: newNote,
        timestamp: now
      }];
      return {
        ...g,
        note: newNote,
        name: newNote || g.name,
        noteHistory: updatedHistory
      };
    }));
    // Record note edit in every condition belonging to this group
    setConditions(prev => prev.map(c => {
      if (c.groupId !== groupId) return c;
      const entry: ConditionHistoryEntry = {
        type: 'note_edited',
        timestamp: now,
        toGroupId: groupId,
        toGroupName: targetGroup?.name || groupId,
        fromNote: oldNote,
        toNote: newNote
      };
      return {
        ...c,
        history: [...(c.history ?? []), entry]
      };
    }));
  };
  const handleCommentGroup = (conditionIds: string[], comment: string) => {
    const now = new Date();
    const newGroupId = crypto.randomUUID();
    const newGroup: ConditionGroup = {
      id: newGroupId,
      name: comment,
      note: comment,
      isExpanded: true,
      createdAt: now,
      noteHistory: [{
        note: comment,
        timestamp: now
      }]
    };
    setGroups(prev => [...prev, newGroup]);
    setConditions(prev => prev.map(c => {
      if (!conditionIds.includes(c.id)) return c;
      const oldGroup = c.groupId ? groups.find(g => g.id === c.groupId) : undefined;
      let entry: ConditionHistoryEntry;
      if (c.groupId) {
        entry = {
          type: 'group_moved',
          timestamp: now,
          fromGroupId: c.groupId,
          fromGroupName: oldGroup?.note || oldGroup?.name || c.groupId,
          toGroupId: newGroupId,
          toGroupName: comment
        };
      } else {
        entry = {
          type: 'group_added',
          timestamp: now,
          toGroupId: newGroupId,
          toGroupName: comment
        };
      }
      return {
        ...c,
        groupId: newGroupId,
        history: [...(c.history ?? []), entry]
      };
    }));
  };
  // Comment Actions
  const handleAddComment = (conditionId: string, text: string, visibility: CommentVisibility) => {
    const now = new Date();
    const newComment: ConditionComment = {
      id: crypto.randomUUID(),
      text,
      visibility,
      timestamp: now
    };
    const historyEntry: ConditionHistoryEntry = {
      type: 'comment_added',
      timestamp: now,
      commentText: text,
      commentVisibility: visibility
    };
    setConditions(prev => prev.map(c => c.id === conditionId ? {
      ...c,
      comments: [...(c.comments ?? []), newComment],
      history: [...(c.history ?? []), historyEntry]
    } : c));
  };
  // Document Actions
  const addDocument = (documentName: string, conditionIds: string[]) => {
    const newDoc: Document = {
      id: crypto.randomUUID(),
      name: documentName,
      conditionIds,
      createdAt: new Date()
    };
    setDocuments(prev => [...prev, newDoc]);
    const now = new Date();
    setConditions(prev => prev.map(c => conditionIds.includes(c.id) ? {
      ...c,
      status: 'cleared' as ConditionStatus,
      satisfiedByDocumentId: newDoc.id,
      history: [...(c.history ?? []), {
        type: 'status_change' as const,
        timestamp: now,
        fromStatus: c.status,
        toStatus: 'cleared' as ConditionStatus
      }]
    } : c));
  };
  return <div className="min-h-screen w-full bg-[#f4f6f9] text-slate-800 font-sans" data-id="element-0">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm" data-id="element-1">
        <div className="max-w-7xl mx-auto px-4 py-3" data-id="element-2">
          <div className="flex items-center justify-between" data-id="element-3">
            <div data-id="element-4" />
            <div className="flex items-center gap-3" data-id="element-5">
              <button onClick={() => setIsNotesHistoryOpen(prev => !prev)} className="px-4 py-1.5 text-sm font-medium rounded-full border transition-colors flex items-center gap-1.5 hover:opacity-80" style={{
              color: isNotesHistoryOpen ? '#ffffff' : '#64748b',
              borderColor: isNotesHistoryOpen ? '#3b82f6' : '#cbd5e1',
              backgroundColor: isNotesHistoryOpen ? '#3b82f6' : '#f8fafc'
            }} data-id="element-6">
                <MessageSquareIcon className="w-3.5 h-3.5" data-id="element-7" />
                Notes History
              </button>
              <button onClick={() => setIsAddingCondition(true)} className="px-4 py-1.5 text-sm font-medium text-[#005fb2] border border-[#005fb2] rounded-full hover:bg-blue-50 transition-colors" data-id="element-8">
                Add Condition(s)
              </button>
              <button onClick={() => setIsDocumentModalOpen(true)} className="px-4 py-1.5 text-sm font-medium bg-[#e3243b] text-white rounded-full hover:bg-red-700 transition-colors shadow-sm" data-id="element-9">
                Upload Document
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6" data-id="element-10">
        <StatusSummaryCards counts={counts} data-id="element-11" />

        <div className="flex items-center justify-between mb-4" data-id="element-12">
          <h2 className="text-xl font-light text-[#005fb2]" data-id="element-13">
            Outstanding Conditions ({activeConditions.length})
          </h2>
        </div>

        {isAddingCondition && <AddConditionForm groups={groups} onAdd={addCondition} onCancel={() => setIsAddingCondition(false)} data-id="element-14" />}

        <div className="space-y-8" data-id="element-15">
          <div className="shadow-sm rounded-lg" data-id="element-16">
            <ConditionTable conditions={activeConditions} groups={groups} documents={documents} onStatusChange={updateConditionStatus} onRemove={removeCondition} onCommentGroup={handleCommentGroup} onMoveToGroup={handleMoveToGroup} onUpdateNote={handleUpdateNote} onAddComment={handleAddComment} showNoteHistory={isNotesHistoryOpen} data-id="element-17" />
          </div>

          {clearedConditions.length > 0 && <div data-id="element-18">
              <h3 className="text-lg font-light text-emerald-700 mb-3" data-id="element-19">
                Cleared Conditions ({clearedConditions.length})
              </h3>
              <div className="shadow-sm rounded-lg opacity-80 hover:opacity-100 transition-opacity" data-id="element-20">
                <ConditionTable conditions={clearedConditions} groups={groups} documents={documents} onStatusChange={updateConditionStatus} onRemove={removeCondition} onCommentGroup={handleCommentGroup} onMoveToGroup={handleMoveToGroup} onUpdateNote={handleUpdateNote} onAddComment={handleAddComment} showNoteHistory={isNotesHistoryOpen} data-id="element-21" />
              </div>
            </div>}
        </div>
      </main>

      <DocumentAssignmentModal isOpen={isDocumentModalOpen} conditions={conditions} groups={groups} onClose={() => setIsDocumentModalOpen(false)} onSubmit={addDocument} data-id="element-22" />
    </div>;
}